import React from 'react';

import { CountBadge } from './CountBadge';
import { Select, SelectContent, SelectItem, SelectTrigger } from './ui/select';

export interface ListFilterOption<V extends string> {
  value: V;
  label: string;
}

export interface ListFilterProps<V extends string> {
  /** Names the dimension being filtered; the trigger's accessible name. */
  label: string;
  value: V;
  onValueChange: (value: V) => void;
  options: ReadonlyArray<ListFilterOption<V>>;
  /**
   * A call to action on ONE option — "4 in review". The CountBadge shows on
   * that option in the menu, and on the trigger only while that option is
   * selected; otherwise the trigger carries a dot with the count as sr-only
   * text, so the number never reads as a count of whatever else is selected.
   */
  attention?: { value: V; count: number; label: string };
}

/**
 * One enumerated filter dimension over a list.
 *
 * Fixed width by construction — pages cannot pass a className — so the
 * trigger does not jump as the selected label changes. Every "all" option
 * names what it covers ("All templates"), never a bare "All".
 *
 * It always goes in DataTable's `filters` slot (before search), wired to
 * `useInfiniteList`'s `filters` / `setFilter` — never in the PageHeader, and
 * never replaced by Radix Tabs (DESIGN_SYSTEM.md §4, §6). Option wording
 * follows what the query returns; where two pages show the same state they
 * take the same options (lib/templateStatus.ts).
 */
export function ListFilter<V extends string>({
  label,
  value,
  onValueChange,
  options,
  attention,
}: ListFilterProps<V>) {
  const selected = options.find((o) => o.value === value);
  const waiting = attention && attention.count > 0 ? attention : null;

  return (
    <Select value={value} onValueChange={(v) => onValueChange(v as V)}>
      <SelectTrigger className="h-9 w-48 shrink-0" aria-label={label}>
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{selected?.label ?? value}</span>
          {waiting && waiting.value === value ? (
            <CountBadge count={waiting.count} />
          ) : waiting ? (
            <>
              <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-info-emphasis" />
              <span className="sr-only">{`${waiting.count} ${waiting.label}`}</span>
            </>
          ) : null}
        </span>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <span className="flex items-center gap-2">
              {option.label}
              {waiting && waiting.value === option.value ? (
                <CountBadge count={waiting.count} />
              ) : null}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
